import React from 'react';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import IconButton from 'aquilla/core/molecules/IconButton';
import Play from 'aquilla/icons/actions/Play';
import Link from '@base/Link/Link';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import P1 from '@base/P1/P1';
import { CLASS_HREF } from './constants';

const VideoCardContainer = styled.div`
  width: 100%;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  margin: ${Spacings.SPACING_5B} ${Spacings.SPACING_0B};
  padding: ${Spacings.SPACING_5B};
  display: flex;
  ${mediaQuery.uptoMobile} {
    border: unset;
    margin: unset;
    padding: ${Spacings.SPACING_10} ${Spacings.SPACING_0B};
    height: 84px;
  }
`;

const PlayIconButton = styled(IconButton)`
  border-radius: 50%;
  height: 40px;
  width: 40px;
  background: ${({ $buttonColor, $isWatched }) =>
    $isWatched ? `${$buttonColor}33` : $buttonColor};
  border: none;
  transition: 0.5s;
  &:hover {
    background: ${({ $buttonColor, $isWatched }) =>
      $isWatched ? `${$buttonColor}33` : $buttonColor};
    transform: scale(1.05);
  }
  ${mediaQuery.uptoMobile} {
    min-height: 40px;
    min-width: 40px;
    svg {
      height: 12px;
      width: 12px;
    }
  }
  svg {
    color: ${({ $buttonColor, $isWatched }) =>
      $isWatched ? $buttonColor : 'var(--color-base-1)'};
  }
`;

const LessonTitle = styled(P1)`
  color: var(--color-i-black);
  font-size: ${Fonts.SMALL};
  font-weight: ${Fonts.SEMIBOLD};
`;

const LessonDescription = styled(P1)`
  color: var(--color-i-gray-13);
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.LIGHT};
`;

const VideoDetails = styled.div`
  margin-left: ${Spacings.SPACING_5B};
  padding-top: ${Spacings.SPACING_5};
`;

const ProgressBarContainer = styled.div`
  width: 40px;
  height: 3px;
  margin-top: ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-i-divider);
`;

const ProgressBar = styled.div`
  width: ${(props) => `${props.$completionPercentage}%`};
  height: 3px;
  border-radius: ${Spacings.SPACING_1B};
  background-color: ${(props) => props.$progressColor};
`;

const getLessonStateString = ({
  learnerWatchProgressPercentage,
  lessonNumber
}) => {
  if (!learnerWatchProgressPercentage) return `Lesson ${lessonNumber}`;
  if (
    learnerWatchProgressPercentage > 0 &&
    learnerWatchProgressPercentage < 100
  )
    return 'Continue watching';
  if (learnerWatchProgressPercentage === 100) return 'Completed';
};

const VideoCard = ({
  video,
  topicName,
  lessonNumber,
  isLoggedIn,
  handleVideoCardClick
}) => {
  const { color } = getTopicGroupAssetObj(topicName);
  const properties = video?.nextSession?.properties ?? {};
  const learnerWatchProgressPercentage =
    (video.learnerWatchProgress /
      (60 * video.nextSession.properties.duration)) *
    100;
  const shouldShowProgressBar =
    isLoggedIn && ![0, 100].includes(learnerWatchProgressPercentage);
  return (
    <Link
      href={CLASS_HREF}
      as={properties?.relativeLink}
      newTab
      onClick={handleVideoCardClick}
    >
      <VideoCardContainer>
        <div>
          <PlayIconButton
            $buttonColor={color}
            $isWatched={learnerWatchProgressPercentage === 100}
          >
            <Play height={12} width={12} />
          </PlayIconButton>
          {shouldShowProgressBar && (
            <ProgressBarContainer>
              <ProgressBar
                $completionPercentage={learnerWatchProgressPercentage}
                $progressColor={color}
              />
            </ProgressBarContainer>
          )}
        </div>
        <VideoDetails>
          <LessonTitle>{video.name}</LessonTitle>
          <LessonDescription>
            {getLessonStateString({
              learnerWatchProgressPercentage,
              lessonNumber
            })}
            {` • ${properties.duration} minutes`}
          </LessonDescription>
        </VideoDetails>
      </VideoCardContainer>
    </Link>
  );
};

export default VideoCard;
